import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import CountBox from "@/components/ui/CountBox";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import Table, { TableColumn } from "@/components/ui/Table";
import { useState, useCallback, useEffect, useMemo } from "react";
import CompanyCodeModal from "@/components/modal/standard/companyCode/CompanyCodeModal";
import { useCompanyCode, useDeleteCompanyCode, useResetShipperPassword } from "@/hooks";
import { StCompanyCode, InsertCompanyCodeRequest } from "@/interface/standard/st_company_code";
import { toast } from "sonner";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";

export default function CompanyCodePage() {
  const [searchValue, setSearchValue] = useState("");
  const [actualSearchValue, setActualSearchValue] = useState(""); // 실제 검색에 사용할 값
  const [exactMatch, setExactMatch] = useState(false);
  const [actualExactMatch, setActualExactMatch] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState<StCompanyCode | undefined>(undefined);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allData, setAllData] = useState<StCompanyCode[]>([]);
  const { mutate: deleteCompanyCode } = useDeleteCompanyCode();
  const { mutate: resetPassword } = useResetShipperPassword();
  const [tableVer, setTableVer] = useState(0);

  // 페이지네이션을 지원하는 useCompanyCode 호출
  const {
    data: companyCodeData,
    isLoading,
    error,
  } = useCompanyCode({
    page: currentPage,
    size: 20,
    sort: "stCompanyCode,desc",
    search: actualSearchValue,
    exactMatch: actualExactMatch,
  });

  // 검색어가 변경될 때 페이지 초기화만 수행 (데이터는 유지)
  useEffect(() => {
    setAllData([]);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
  }, [actualSearchValue, actualExactMatch]);

  // 새로운 페이지 데이터가 로드되면 누적 데이터에 추가
  useEffect(() => {
    if (companyCodeData && companyCodeData.content && companyCodeData.content.length > 0) {
      // 첫 번째 페이지(currentPage === 0)이거나 검색이 변경된 경우 기존 데이터 교체
      if (currentPage === 0) {
        setAllData(companyCodeData.content);
        setTotalElements(companyCodeData.totalElements || 0);
      } else {
        // 추가 페이지는 기존 데이터에 추가
        setAllData(prev => [...prev, ...companyCodeData.content]);
      }
    } else if (companyCodeData && (!companyCodeData.content || companyCodeData.content.length === 0)) {
      // 데이터가 없는 경우 빈 배열로 설정
      setAllData([]);
      setTotalElements(0);
    }
  }, [companyCodeData, currentPage]); // actualSearchValue 의존성 제거

  // hasMore 상태를 useMemo로 계산
  const hasMore = useMemo(() => {
    if (!companyCodeData) return false;
    return !companyCodeData.last && allData.length < totalElements;
  }, [companyCodeData?.last, allData.length, totalElements]);

  // Table의 onLoadMore 콜백
  const handleTableLoadMore = useCallback(
    async (currentCount: number) => {
      if (!hasMore || loadingMore) {
        return;
      }

      // 현재 표시된 데이터가 누적된 데이터의 80% 이상일 때 다음 페이지 요청
      if (currentCount >= allData.length * 0.8) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
    [hasMore, loadingMore, allData.length]
  );

  // 로딩 상태 업데이트
  useEffect(() => {
    if (companyCodeData) {
      setLoadingMore(false);
    }
  }, [companyCodeData]);

  // 선택된 행 데이터 관리
  useEffect(() => {
    if (selectedRows.length === 1) {
      // 1개만 선택된 경우 해당 데이터 설정
      const idx = selectedRows[0];
      if (idx >= 0 && idx < allData.length) {
        setSelectedCompanyCode(allData[idx]);
      }
    } else if (!isEditModalOpen) {
      // 모달 열려있으면 유지
      setSelectedCompanyCode(undefined);
    }
  }, [selectedRows, allData, isEditModalOpen]);

  // 테이블 컬럼 정의
  const columns: TableColumn[] = [
    { key: "stCompanyCode", header: "화주ID" },
    { key: "stCompanyName", header: "업체명", bodyAlign: "left" },
    { key: "stCompanyNameshort", header: "줄임상호", bodyAlign: "left" },
    { key: "stCompanyNumber", header: "사업자번호" },
    { key: "stCompanyOwnername", header: "대표자", bodyAlign: "left" },
    {
      key: "stCompanyTel",
      header: "전화번호",
    },
    { key: "stCompanyFax", header: "팩스번호" },
    { key: "stCompanyMovetel", header: "담당자 번호" },
    {
      key: "stCompanyType",
      header: "구분",
      render: (v, r, i) => {
        return <>{v === 1 ? "화주사" : "운전사"}</>;
      },
    },
    { key: "stCompanyTaddress1", header: "주소지", bodyAlign: "left" },
    { key: "stCompanyRemark", header: "비고", bodyAlign: "left" },
  ];

  const handleReset = () => {
    setSearchValue("");
    setActualSearchValue(""); // 실제 검색값도 초기화
    setExactMatch(false);
    setActualExactMatch(false);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
    setAllData([]);
  };

  const handleSearch = useCallback(() => {
    // 조회 버튼을 누를 때만 실제 검색값 설정
    setActualSearchValue(searchValue);
    setActualExactMatch(exactMatch);
  }, [searchValue, exactMatch]);

  const handleEditClick = () => {
    if (selectedRows.length === 0) {
      toast.warning("수정할 항목을 선택해주세요.");
      return;
    }

    if (selectedRows.length > 1) {
      toast.warning("수정할 항목은 1개만 선택해주세요.");
      return;
    }

    // 1개만 선택된 경우 수정 모달 열기
    setSelectedCompanyCode(allData[selectedRows[0]]);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = () => {
    if (selectedRows.length === 0) {
      toast.warning("삭제할 항목을 선택해주세요.");
      return;
    }

    const selectedData = selectedRows.map(index => allData[index]);
    const idsToDelete = selectedData.map(c => c.stCompanyCode);

    // 서버 요청
    deleteCompanyCode(idsToDelete);

    // 로컬 상태 즉시 반영
    setAllData(prev => prev.filter(item => !idsToDelete.includes(item.stCompanyCode!)));

    setSelectedRows([]);
    setSelectedCompanyCode(undefined);
    setTableVer(v => v + 1);
  };

  const handleResetPasswordClick = () => {
    if (selectedRows.length === 0) {
      toast.warning("비밀번호를 초기화할 항목을 선택해주세요.");
      return;
    }

    if (selectedRows.length > 1) {
      toast.warning("비밀번호 초기화는 1개만 선택해주세요.");
      return;
    }

    const selectedData = allData[selectedRows[0]];
    if (!selectedData.stCompanyCode) {
      toast.error("업체 코드가 없습니다.");
      return;
    }

    // 확인 후 초기화
    if (
      window.confirm(
        `${selectedData.stCompanyName || selectedData.stCompanyCode}의 비밀번호를 1234로 초기화하시겠습니까?`
      )
    ) {
      resetPassword(String(selectedData.stCompanyCode));
    }
  };

  const handleModalSave = useCallback(
    (data: InsertCompanyCodeRequest, mode: "add" | "edit") => {
      if (mode === "edit" && selectedCompanyCode?.stCompanyCode) {
        // 선택된 항목의 id 기준으로 로컬 리스트 즉시 갱신
        setAllData(prev =>
          prev.map(item =>
            item.stCompanyCode === selectedCompanyCode.stCompanyCode
              ? { ...item, ...data } // 변경 필드 덮어쓰기
              : item
          )
        );

        // 선택/모달 상태 정리 + 테이블 리렌더
        setIsEditModalOpen(false);
        setSelectedRows([]);
        setSelectedCompanyCode(undefined);
        setTableVer(v => v + 1);
      } else if (mode === "add") {
        setAllData(prev => [data as StCompanyCode, ...prev]); // 즉시 반영
        setIsAddModalOpen(false);
        setTableVer(v => v + 1); // 테이블 강제 리렌더
      }
    },
    [selectedCompanyCode]
  );

  // 로딩 중일 때 표시할 내용
  if (isLoading && allData.length === 0) {
    return <div className="flex items-center justify-center h-full">로딩 중...</div>;
  }

  // 에러가 발생했을 때 표시할 내용
  if (error && allData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        데이터를 불러오는 중 오류가 발생했습니다.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex gap-3 items-end">
          <InputBox
            label="검색어"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className="w-50"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={allData.length} />
          <div className="flex gap-2">
            <PermissionButton permission="canUpdate" variant="primary" width="120px" onClick={handleResetPasswordClick}>
              비밀번호 초기화
            </PermissionButton>
            <PermissionButton
              permission="canCreate"
              variant="etc"
              onClick={() => {
                setSelectedRows([]);
                setSelectedCompanyCode(undefined);
                setTableVer(v => v + 1);
                setIsAddModalOpen(true);
              }}
            >
              추가
            </PermissionButton>
            <PermissionButton permission="canUpdate" variant="etc" onClick={handleEditClick}>
              수정
            </PermissionButton>
            <PermissionButton permission="canDelete" variant="etc" onClick={handleDeleteClick}>
              삭제
            </PermissionButton>
            <PrintButton data={allData} columns={columns} title="거래처코드" />
            <ExcelButton data={allData} columns={columns} fileName="거래처코드" />
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <Table
            key={`company-table-v${tableVer}`}
            className="h-full"
            columns={columns}
            data={allData}
            showCheckbox={true}
            showRowNumber={true}
            selectedRows={selectedRows}
            onRowSelect={setSelectedRows}
            onRowClick={(row, index) => {
              console.log(selectedRows);
            }}
            maxHeight="100%"
            infiniteScroll={true}
            initialLoadCount={20}
            loadMoreCount={20}
            hasMore={hasMore}
            onLoadMore={handleTableLoadMore}
            loadingMore={loadingMore}
          />
        </div>
        {isLoading && allData.length > 0 && <div className="text-center py-2 text-gray-500">새 페이지 로딩 중...</div>}
      </main>
      <CompanyCodeModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleModalSave}
        mode="add"
        initialData={undefined}
      />
      <CompanyCodeModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedRows([]); // 모달 닫을 때 체크박스 해제
          setSelectedCompanyCode(undefined); // 모달 닫을 때 체크박스 초기화
        }}
        onSave={handleModalSave}
        mode="edit"
        initialData={selectedCompanyCode}
      />
    </div>
  );
}
